import { DefaultTable } from 'Components/common/DefaultTable';
import { Main as Container } from 'Components/Main';
import { getTableHeader, getDetailColumn } from '../table-header.js';
import { AuditLogDetailDialog } from '../AuditLogDetailDialog.jsx';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';
import { Controller } from 'react-hook-form';
import { DefaultPagination } from 'Components/common/DefaultPagination';
import { Button, PageHeader, Select, TextField } from 'Components/ui';
import { useAuditLogQuery } from '../useAuditLogQuery.js';
import { SearchSvg } from '../SearchSvg.jsx';
import * as S from '../styles.js';

// Tenant-facing audit log — the counterpart to Global/index.jsx (the VO-only, cross-tenant
// Config Global view). This page has zero company filter/column and zero VO-role gating of
// its own: it is structurally absent here, not conditionally hidden, and reachability for
// the Global view is enforced at the route level (see routing.js's onlyAdminVO on
// /audit-logs/global), not inside either component.
export const AuditLogsTenant = () => {
  const {
    t,
    companyTimezone,
    companyLocale,
    refetch,
    setRefetch,
    page,
    setPage,
    isExporting,
    selectedLog,
    setSelectedLog,
    register,
    control,
    reset,
    isLoading,
    error,
    pagedLogs,
    handleExportCsv,
  } = useAuditLogQuery();

  const tableHeader = getTableHeader(t, companyTimezone, companyLocale, [
    getDetailColumn(t, setSelectedLog),
  ]);

  return (
    <Container>
      <PageHeader
        title={t('audit:page.title')}
        segments={[
          { label: t('common:breadcrumbs.manage') },
          { label: t('audit:page.title') },
        ]}
        actions={
          <Button variant="outline" onClick={handleExportCsv} disabled={isExporting}>
            {isExporting ? 'Exportando...' : 'Exportar CSV'}
          </Button>
        }
      />
      {error && <div>Ocorreu um erro ...</div>}
      {!error && (
        <>
          <S.FilterCard>
            <S.FilterCardLabel>{t('audit:page.filters.label')}</S.FilterCardLabel>
            <S.FilterRow>
              <S.FilterField>
                <S.FilterFieldLabel htmlFor="audit-filter-startDate">{t('audit:page.filters.startDate')}</S.FilterFieldLabel>
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field }) => (
                    <TextField
                      id="audit-filter-startDate"
                      type="date"
                      fullWidth
                      {...field}
                      value={field.value || ''}
                    />
                  )}
                />
              </S.FilterField>
              <S.FilterField>
                <S.FilterFieldLabel htmlFor="audit-filter-endDate">{t('audit:page.filters.endDate')}</S.FilterFieldLabel>
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field }) => (
                    <TextField
                      id="audit-filter-endDate"
                      type="date"
                      fullWidth
                      {...field}
                      value={field.value || ''}
                    />
                  )}
                />
              </S.FilterField>
              <S.FilterField>
                <S.FilterFieldLabel id="service-label">{t('audit:page.filters.service')}</S.FilterFieldLabel>
                <Controller
                  control={control}
                  name="service"
                  render={({ field }) => (
                    <Select
                      name="service"
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { value: '', label: 'Todos os serviços' },
                        { value: 'user', label: 'user' },
                        { value: 'ga', label: 'ga' },
                        { value: 'mapping', label: 'mapping' },
                        { value: 'policy', label: 'policy' },
                        { value: 'vendor', label: 'vendor' },
                        { value: 'company', label: 'company' },
                        { value: 'cspm', label: 'cspm' },
                        { value: 'tc', label: 'tc' },
                        { value: 'asm', label: 'asm' },
                        { value: 'cookie', label: 'cookie' },
                        { value: 'mcp', label: 'mcp' },
                      ]}
                    />
                  )}
                />
              </S.FilterField>
            </S.FilterRow>
            <S.FilterRow $last>
              <S.FilterField>
                <S.FilterFieldLabel id="typeFilter-label">{t('audit:page.filters.filterBy')}</S.FilterFieldLabel>
                <Controller
                  control={control}
                  name="typeFilter"
                  render={({ field }) => (
                    <Select
                      name="typeFilter"
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { value: '', label: 'Selecione o campo' },
                        ...['Usuário', 'Ação', 'Resposta'].map((item) => ({ value: item, label: item })),
                      ]}
                    />
                  )}
                />
              </S.FilterField>
              <S.FilterField>
                <S.FilterFieldLabel htmlFor="audit-filter-searchContent">{t('audit:page.filters.search')}</S.FilterFieldLabel>
                <TextField
                  id="audit-filter-searchContent"
                  {...register('searchContent')}
                  fullWidth
                />
              </S.FilterField>
              <S.FilterField>
                <S.FilterFieldLabel id="actorType-label">{t('audit:page.filters.actorType')}</S.FilterFieldLabel>
                <Controller
                  control={control}
                  name="actorType"
                  render={({ field }) => (
                    <Select
                      name="actorType"
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { value: '', label: 'Todos os tipos' },
                        { value: 'user', label: 'Usuário' },
                        { value: 'system', label: 'Sistema' },
                        { value: 'external', label: 'Externo' },
                        { value: 'ai_agent', label: 'Agente de IA' },
                      ]}
                    />
                  )}
                />
              </S.FilterField>
            </S.FilterRow>
            <S.FilterActions>
              <Button variant="secondary" size="sm" type="button" sx={S.FilterButtonSx}
                onClick={() => { reset(); setRefetch(!refetch); }}>
                {t('common:buttons.clear')}
              </Button>
              <Button variant="primary" size="sm" type="button" sx={S.FilterButtonSx} startIcon={<SearchSvg />}
                onClick={() => { setRefetch(!refetch); }}>
                {t('common:buttons.filter')}
              </Button>
            </S.FilterActions>
          </S.FilterCard>
          {!isLoading && pagedLogs.data.length === 0 && <NoData description={'Não há logs para exibir'} />}
          {!isLoading && pagedLogs.data?.length > 0 && (
            <div className="container mt-3">
              <DefaultTable
                rows={tableHeader}
                cells={pagedLogs.data}
                onClickDelete={() => {}}
                hideDelete
                hideEdit
                hideShow
                link="logs"
              />
              <S.PaginationWrapper>
                <DefaultPagination count={pagedLogs.totalPages} page={page} color="primary"
                  showFirstButton showLastButton
                  onChange={(e, selectedPage) => { setPage(selectedPage); }} />
              </S.PaginationWrapper>
            </div>
          )}
        </>
      )}
      {isLoading && <PageLoaderList></PageLoaderList>}
      <AuditLogDetailDialog
        log={selectedLog}
        t={t}
        companyTimezone={companyTimezone}
        companyLocale={companyLocale}
        onClose={() => setSelectedLog(null)}
      />
    </Container>
  );
};
